// Pure view logic for the Lead Log (docs/lead-relay.md §10.2).
//
// ⚠️⚠️ THE ROW DETAIL IS FOR A PERSON RE-KEYING A FAILED LEAD BY HAND, not for a
// developer debugging one. That is the fallback the whole design rests on (§6):
// if VirtualYard blocks us, someone works the backlog from this screen. So the
// customer's details come first, as labelled values in the order VirtualYard's
// form asks for them — never as a JSON dump. The attempts are below, for
// diagnosis.

import type {
  EditableLeadField,
  LeadFailureReason,
  LeadInterestFallback,
  LeadLogRow,
  LeadProvider,
  LeadSubmission,
  LeadSubmissionStatus,
} from '../../types/leadRelay.js';
import { NAME_PLACEHOLDER, resolveLeadName, submissionLeadName } from './names.js';
import { anytimeFitnessSent, virtualYardSent } from './sent.js';
import { PROVIDER_VOCABULARY, systemName } from './providerVocabulary.js';

export const STATUS_LABELS: Record<LeadSubmissionStatus, string> = {
  unassigned: 'Unassigned',
  received: 'Received',
  submitted: 'Submitted',
  retrying: 'Retrying',
  failed: 'Failed',
  keyed_manually: 'Keyed in by hand',
  test: 'Test mode (not sent)',
  duplicate: 'Duplicate',
};

/**
 * Every status, in display order. Derived from STATUS_LABELS (a Record, so a
 * new status cannot be added without a label) — the Lead Log's filter and the
 * CRM router's query parser both read this, rather than each keeping a list.
 */
export const LEAD_SUBMISSION_STATUSES = Object.keys(STATUS_LABELS) as LeadSubmissionStatus[];

/**
 * What the Lead Log says about the Rawdata parse on one lead. Each line is
 * something a person should know about what reached the dealer. (A Rawdata
 * that did not parse at all is an unassigned REASON, not a note — assignment.ts.)
 */
export function payloadNotes(sub: Pick<LeadSubmission, 'rawdata'>): string[] {
  const notes: string[] = [];
  const r = sub.rawdata;
  if (r && r.keysBeyondCap > 0) {
    // ⚠️ Loud on purpose: a form we do not understand, partly DISCARDED.
    notes.push(
      `This lead's rawdata had ${r.keysBeyondCap} more answers than the relay reads — they were discarded and did NOT reach the dealer. A form this large needs looking at.`,
    );
  }
  if (r && r.unreadTopLevelKeys.length > 0) {
    notes.push(
      `The Zap's rawdata carried ${r.unreadTopLevelKeys.join(', ')}, which is neither a form answer nor part of Meta's lead envelope — its value did NOT reach the dealer.`,
    );
  }
  if (r && r.fallbackKeys.length > 0) {
    // Not an FYI: an instruction. The value is NOT prettified and no label is guessed.
    notes.push(
      `${r.fallbackKeys.join(', ')} had no label in the payload, so Meta's raw option key (underscores and all) reached the dealer.`,
    );
  }
  return notes;
}

const NAME_FIELD_LABEL = { first_name: 'first name', last_name: 'last name' } as const;

/**
 * What the Lead Log says about how this lead's NAME was resolved (names.ts):
 * a split from a full name, and which fields go out as NAME_PLACEHOLDER. Read
 * from the stored record when the lead was composed; for one never composed
 * (an unassigned lead) it is worked out from the input, so the operator about
 * to assign it already knows. ⚠️ The placeholder is never in our record — this
 * line is the only place it appears.
 */
export function nameNotes(sub: Pick<LeadSubmission, 'nameResolution' | 'input' | 'provider'>): string[] {
  const r = sub.nameResolution ?? resolveLeadName(sub.input).resolution;
  if (!r) return [];
  const notes: string[] = [];
  if (r.splitFrom !== null && r.derived.length > 0) {
    const parts = resolveLeadName(sub.input);
    const got = r.derived.map((f) => `${NAME_FIELD_LABEL[f]} "${f === 'first_name' ? parts.first : parts.last}"`).join(' and ');
    notes.push(`The form gave a full name only (${r.splitFrom}: "${r.fullName}"), so the relay split it: ${got} — derived, not typed by the lead.`);
  }
  if (r.placeholder.length > 0) {
    const fields = r.placeholder.map((f) => NAME_FIELD_LABEL[f]).join(' and ');
    const to = sub.provider === null ? 'whichever system it is assigned to' : systemName(sub.provider);
    const verb = r.placeholder.length > 1 ? 'are' : 'is';
    notes.push(`The ${fields} ${verb} empty — the form never supplied it — so "${NAME_PLACEHOLDER}" is sent to ${to} in its place. Our record keeps it empty.`);
  }
  return notes;
}

/**
 * Why a lead failed, in words — naming the system that ACTUALLY refused it
 * (2026-10-07). It was a constant table reading "VirtualYard" on every failure,
 * including an Anytime Fitness one; an operator reading a failure at 2am needs
 * the name of the system that said no. ⚠️ A function of the lead's provider,
 * deliberately not a table — there is no provider-free label to reach for. A
 * lead with no provider gets the neutral phrase (systemName(null)), never a
 * guessed system. `Record<LeadFailureReason, …>` keeps a new reason a compile
 * error until it has words.
 */
const FAILURE_WORDING: Record<LeadFailureReason, (system: string) => string> = {
  missing_required: () => 'Missing required fields',
  no_route: () => 'Its route is missing or inactive',
  form_changed: (system) => `${system} form changed`,
  provider_rejected: (system) => `${system} rejected it`,
  provider_unrecognised_response: (system) => `${system}'s response was not recognised — it may not have arrived`,
  delivered_wrong_club: (system) => `${system} created it at a DIFFERENT club than this route's — needs a person`,
  network: (system) => `Could not reach ${system}`,
  blocked_default_interest: () => 'Route default interest is blocked',
  invalid_route: () => 'Route submit URL is invalid',
  provider_not_wired: (system) => `${system} cannot be sent to yet`,
  provider_blocked: (system) =>
    `${system}'s bot protection blocked the relay — not a problem with the lead (needs a static egress IP they allowlist)`,
};

export function failureLabel(reason: LeadFailureReason, provider: LeadProvider | null): string {
  return FAILURE_WORDING[reason](systemName(provider));
}

export const INTEREST_FALLBACK_LABELS: Record<LeadInterestFallback, string> = {
  none: 'No model given — route default sent',
  unmapped: 'Model answer not recognised — route default sent',
  not_allowed: 'Model not allowed on this route — route default sent',
  blocked: 'Blocked option — route default sent',
  not_on_form: 'Option no longer on the form — route default sent',
};

/** Still going to be attempted by the retry sweep. */
export function isInFlight(status: LeadSubmissionStatus): boolean {
  return status === 'received' || status === 'retrying';
}

/**
 * ⚠️ A lead already in flight keeps retrying after its route is switched to
 * test mode (test mode stops NEW leads only — step 1 §16). Somebody pausing a
 * store would otherwise be surprised by a submission from it, so the log says
 * so on the row.
 */
export function willSubmitDespiteTestMode(row: Pick<LeadLogRow, 'status' | 'routeTestMode'>): boolean {
  return isInFlight(row.status) && row.routeTestMode === true;
}

export function testModeInFlightNote(provider: LeadProvider | null): string {
  return `This lead was already queued when the route was put in test mode. Test mode stops NEW leads only, so it will still be submitted to ${systemName(provider)}.`;
}

export interface RekeyField {
  label: string;
  value: string;
  /** Shown for fields the lead's PROVIDER requires, so a gap is obvious. Never true with no provider. */
  required: boolean;
  /** Shown (muted) in place of an empty, non-required value — e.g. "Not assigned yet". */
  placeholder?: string;
}

/**
 * The fields in the order the lead's provider asks for them (§10.2) — the
 * screen someone re-keys from by hand.
 *
 * ⚠️⚠️ PROVIDER-AWARE (step 3, 2026-10-06). The destination's label ("Store" /
 * "Club"), which fields are required and by whom, all come from the lead's
 * provider. An UNASSIGNED lead has NO provider yet, so nothing on it is
 * "required by" anyone and its destination reads "Not assigned yet" — it once
 * read "Store: Missing — VirtualYard requires this" on a lead bound nowhere.
 */
export function rekeyFields(submission: LeadSubmission): RekeyField[] {
  const input = submission.input;
  const store = submission.resolvedStore;
  const provider = submission.provider;
  const name = submissionLeadName(submission);
  if (provider === null) {
    return [
      { label: 'First name', value: name.first, required: false },
      { label: 'Last name', value: name.last, required: false },
      { label: 'Mobile', value: input.phone ?? '', required: false },
      { label: 'Email', value: input.email ?? '', required: false },
      { label: 'Postcode', value: input.postcode ?? '', required: false },
      { label: 'Destination', value: '', required: false, placeholder: 'Not assigned yet' },
      { label: 'Model', value: input.model ?? '', required: false },
    ];
  }
  const destination = {
    label: PROVIDER_VOCABULARY[provider].destination,
    value: store ? `${store.name} (${store.lid})` : '',
    required: true,
  };
  switch (provider) {
    case 'virtualyard': {
      // Values from what was COMPOSED for the form when there is one (the exact
      // strings that would have been sent), falling back to what the lead typed.
      const sent = virtualYardSent(submission.sent);
      return [
        // Names are never required: an empty one goes out as NAME_PLACEHOLDER (names.ts).
        { label: 'First name', value: name.first, required: false },
        { label: 'Last name', value: name.last, required: false },
        { label: 'Mobile', value: sent?.phone ?? input.phone ?? '', required: false },
        { label: 'Email', value: sent?.email ?? input.email ?? '', required: true },
        { label: 'Postcode', value: sent?.postcode ?? input.postcode ?? '', required: true },
        destination,
        { label: 'Interested in', value: sent?.interestedin ?? input.model ?? '', required: false },
        { label: 'Marketing opt-in', value: sent?.optin ?? 'no', required: false },
        { label: 'Message', value: sent?.message ?? '', required: true },
      ];
    }
    case 'anytimefitness': {
      // ⚠️ AF requires the MOBILE and not the postcode — the reverse of
      // VirtualYard. No interest, opt-in or message: AF has none of them.
      // Values from what was COMPOSED when there is one (the mobile as the
      // E.164 AF was sent), falling back to what the lead typed.
      const p = anytimeFitnessSent(submission.sent)?.prospectData;
      return [
        { label: 'First name', value: name.first, required: false },
        { label: 'Last name', value: name.last, required: false },
        { label: 'Mobile', value: p?.mobile_phone || input.phone || '', required: true },
        { label: 'Email', value: p?.email ?? input.email ?? '', required: true },
        { label: 'Postcode', value: p?.address_data?.zip ?? input.postcode ?? '', required: false },
        destination,
      ];
    }
    default:
      return assertNever(provider);
  }
}

function assertNever(value: never): never {
  throw new Error(`Unhandled lead provider: ${JSON.stringify(value)}`);
}

/** The gap text beside a required field left empty — naming who requires it. */
export function requiredGapText(provider: LeadProvider): string {
  return `Missing — ${PROVIDER_VOCABULARY[provider].system} requires this`;
}

export function leadDisplayName(firstName: string | undefined, lastName: string | undefined): string {
  const name = [firstName, lastName].map((v) => (v ?? '').trim()).filter(Boolean).join(' ');
  return name === '' ? '(no name)' : name;
}

/**
 * A submission's display name, through the ONE resolver (`submissionLeadName`).
 * ⚠️ Never call `leadDisplayName` with raw input fields: a full_name-only form
 * has none, and that is how a delivered lead read "(no name)" in the list.
 */
export function submissionDisplayName(submission: Pick<LeadSubmission, 'input' | 'sent'>): string {
  const { first, last } = submissionLeadName(submission);
  return leadDisplayName(first, last);
}

/** Statuses shown by default. Duplicates are records of a re-fire, not leads. */
export const DEFAULT_LOG_STATUSES: readonly LeadSubmissionStatus[] = [
  'unassigned',
  'received',
  'submitted',
  'retrying',
  'failed',
  'keyed_manually',
  'test',
];

/**
 * ⚠️ Failed only. A keyed-in-by-hand lead is NEVER resendable: the dealer
 * already has it, so a resend would be a guaranteed duplicate rather than a
 * possible one. The relay refuses it too (resend requires `failed`).
 */
export function canResend(status: LeadSubmissionStatus): boolean {
  return status === 'failed';
}

/**
 * ⚠️ Failed only, and the SAME predicate as Resend's: editing a lead's answers is
 * only ever the step before a Resend. A submitted lead is the dealer's already
 * (a resend would be a second lead); an unassigned lead's fix is Assign and
 * send; a retrying one may still land on its own.
 */
export function canEditLead(status: LeadSubmissionStatus): boolean {
  return status === 'failed';
}

/**
 * ⚠️⚠️ THE FIELDS A PERSON MAY CHANGE ON A FAILED LEAD — a literal list of
 * LeadInput keys, NOT `rekeyFields` minus a few. rekeyFields reads `sent` first
 * and several of its rows are COMPOSED values (Interested in = the model through
 * the route's model map, Message = the route template, opt-in = derived from
 * `optin`): an edit box on one would look like it worked and be rebuilt on the
 * next compose. `satisfies Record<LeadProvider, …>` makes a third provider fail
 * to compile until someone states what is editable on it.
 *
 * Deliberately absent from every list: `optin` (a recorded consent answer is not
 * a typo to fix), and meta_lead_id / form_name / campaign_name / platform /
 * extra, which record how the lead ARRIVED. The destination (`lid`) is not here
 * because it is route config, not a LeadInput key — keep it that way.
 */
export const EDITABLE_LEAD_FIELDS = {
  virtualyard: ['first_name', 'last_name', 'email', 'phone', 'postcode', 'model'],
  anytimefitness: ['first_name', 'last_name', 'email', 'phone', 'postcode'],
} as const satisfies Record<LeadProvider, readonly EditableLeadField[]>;

export const EDITABLE_FIELD_LABELS: Record<EditableLeadField, string> = {
  first_name: 'First name',
  last_name: 'Last name',
  email: 'Email',
  phone: 'Mobile',
  postcode: 'Postcode',
  model: 'Model',
};

/** The fields editable on a lead, by its provider; none for a lead with no provider. */
export function editableFieldsFor(provider: LeadProvider | null): readonly EditableLeadField[] {
  return provider === null ? [] : EDITABLE_LEAD_FIELDS[provider];
}

function editDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'Australia/Brisbane' });
}

/**
 * What the Lead Log says about fields a PERSON changed on this lead, in the same
 * place and voice as nameNotes. ⚠️ SUPPLIED and CORRECTED read differently, and
 * that is the point: a stand-in value typed where the lead gave none will sit in
 * the dealer's CRM looking exactly like a customer's answer, and this line is
 * the only place the record says otherwise.
 */
export function editNotes(sub: Pick<LeadSubmission, 'inputEdits'>): string[] {
  const notes: string[] = [];
  for (const edit of sub.inputEdits ?? []) {
    const when = editDate(edit.at);
    for (const c of edit.changes) {
      const label = EDITABLE_FIELD_LABELS[c.field] ?? c.field;
      if (c.from === '') {
        notes.push(`${label} ${c.to} was entered by ${edit.byEmail} on ${when} — the lead gave none.`);
      } else if (c.to === '') {
        notes.push(`${label} was cleared by ${edit.byEmail} on ${when} (was ${c.from}).`);
      } else {
        notes.push(`${label} corrected by ${edit.byEmail} on ${when} (was ${c.from}).`);
      }
    }
  }
  return notes;
}

/**
 * ⚠️ An edit saved on a failed lead that no attempt has followed. The lead still
 * reads FAILED with its old missingFields — honest, since nothing has been sent —
 * so the panel says "Resend to apply this" rather than showing a success state.
 * (resend() clears missingFields itself.)
 */
export function hasUnsentEdit(sub: Pick<LeadSubmission, 'status' | 'inputEdits' | 'attempts'>): boolean {
  if (!canEditLead(sub.status)) return false;
  const edits = sub.inputEdits ?? [];
  if (edits.length === 0) return false;
  const lastEdit = edits[edits.length - 1]?.at ?? '';
  const lastAttempt = sub.attempts[sub.attempts.length - 1]?.at ?? '';
  return lastEdit > lastAttempt;
}

/**
 * Failed or UNASSIGNED, and deliberately not `retrying`: a retrying lead may
 * still land on its own, so marking it keyed would leave the sweep submitting a
 * lead a person has already entered. Wait for it to fail, or let it succeed.
 *
 * ⚠️ An UNASSIGNED lead can be keyed too — typically one with no usable Rawdata,
 * which has nothing to send — and doing so takes it OUT of the Unassigned queue
 * for good: keyed_manually is terminal, never sent, never assignable.
 */
export function canMarkKeyedManually(status: LeadSubmissionStatus): boolean {
  return status === 'failed' || status === 'unassigned';
}

/**
 * ⚠️⚠️ ADMIN-ONLY ON AN UNASSIGNED LEAD, matching Assign and send. Keying an
 * unassigned lead in by hand is deciding which dealership received a member of
 * the public's details — the decision a route, and Assign and send, make, and
 * both are admin-only. A FAILED lead already had a route chosen for it, so any
 * staff may record that they re-keyed it (the Resend posture).
 */
export function keyedManuallyNeedsAdmin(status: LeadSubmissionStatus): boolean {
  return status === 'unassigned';
}

/** May THIS viewer mark this lead — the state rule and the role rule together. */
export function mayMarkKeyedManually(status: LeadSubmissionStatus, isAdmin: boolean): boolean {
  return canMarkKeyedManually(status) && (isAdmin || !keyedManuallyNeedsAdmin(status));
}

/** ⚠️ An unassigned lead has no route, so the note is the only record of where it went. */
export function keyedManuallyNoteRequired(status: LeadSubmissionStatus): boolean {
  return status === 'unassigned';
}

/** The longest keyed-manually note the server accepts. */
export const KEYED_MANUALLY_NOTE_MAX = 1000;

/** Unassigned only — the one state Assign and send acts on. A lead with a route is never re-assigned. */
export function canAssign(status: LeadSubmissionStatus): boolean {
  return status === 'unassigned';
}

/**
 * The Assign-and-send confirmation. ⚠️ It says that the click SENDS — there is
 * no assigning without sending — and to whom, by name.
 */
export function assignConfirmation(submission: LeadSubmission, routeName: string): string[] {
  const { customer } = confirmationParties(submission, routeName);
  return [
    `Send ${customer}'s enquiry to ${routeName}?`,
    'This assigns the lead to that route AND sends it to the dealer now. It cannot be re-assigned afterwards, so check it is the right dealership.',
  ];
}

/** The dealer and the customer a confirmation names. */
export function confirmationParties(
  submission: LeadSubmission,
  routeName: string | null,
): { dealer: string; customer: string } {
  const dealer = submission.resolvedStore?.name ?? routeName ?? 'the dealer (no store resolved)';
  const name = submissionDisplayName(submission);
  const contact = [submission.input.email, submission.input.phone].map((v) => (v ?? '').trim()).filter(Boolean);
  return { dealer, customer: contact.length > 0 ? `${name} (${contact.join(', ')})` : name };
}

/**
 * The Resend confirmation (§20). Resend stays open to any staff — gating it
 * would slow the one recovery path the design exists to protect, and a
 * duplicate enquiry beats a lost lead — so the confirmation is where the
 * duplicate risk is stated, by name, before the click.
 */
export function resendConfirmation(submission: LeadSubmission, routeName: string | null): string[] {
  const { dealer, customer } = confirmationParties(submission, routeName);
  const lines = [`Send ${customer}'s enquiry to ${dealer} through the relay again?`];
  if (submission.attempts.some((a) => a.outcome === 'unknown')) {
    lines.push(
      `An earlier attempt timed out and MAY have reached ${systemName(submission.provider)}. If it did, the dealer will get this enquiry twice.`,
    );
  }
  lines.push(
    'If someone has already keyed it in by hand, mark it as keyed instead — resending would send it twice. A duplicate is better than a lost lead, but check first.',
  );
  return lines;
}

/** The Mark-keyed-manually confirmation (§20). */
export function keyedManuallyConfirmation(submission: LeadSubmission, routeName: string | null): string[] {
  const { dealer, customer } = confirmationParties(submission, routeName);
  if (submission.status === 'unassigned') {
    return [
      `Confirm that ${customer}'s enquiry has been keyed in by hand?`,
      'It has no route, so it leaves the Unassigned queue for good: it will never be sent and can no longer be assigned. Say in the note where you keyed it in. Your name is recorded against it.',
    ];
  }
  return [
    `Confirm that ${customer}'s enquiry has been keyed into ${dealer}'s ${submission.provider ? systemName(submission.provider) : 'system'} by hand?`,
    'It will stop counting as a failure on the account tile and can no longer be resent. Your name is recorded against it.',
  ];
}
